import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, AlertOctagon, Hourglass, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useClientTheme } from "@/contexts/ThemeContext";

export default function AdminRecover() {
  const { i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { theme } = useClientTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const launchToken = params.get("token") || "";
  const isLaunch = params.get("action") === "launch" || !!launchToken;

  const [stage, setStage] = useState(isLaunch ? "launch" : "request");
  const [blocked, setBlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [precode, setPrecode] = useState("");
  const [launchCode, setLaunchCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/admin/recovery/status").then((r) => setBlocked(!!r.data?.blocked)).catch(() => {});
  }, []);

  if (blocked) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-5" data-theme={theme}>
        <div className="absolute top-5 right-5 z-10"><ThemeSwitcher /></div>
        <div className="max-w-md w-full bg-red-950/40 border border-red-500/50 rounded-xl p-6 space-y-4" data-testid="admin-recover-blocked">
          <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-red-300">
            <AlertOctagon className="w-6 h-6" /> {isEN ? "Recovery blocked" : "Recupero bloccato"}
          </h1>
          <p className="text-sm text-red-100">
            {isEN
              ? "The admin has blocked recovery attempts. Your fraudulent attempt has been detected."
              : "L'admin ha bloccato i tentativi di recupero. Il tuo tentativo fraudolento è stato scoperto."}
          </p>
          <Link to="/login" className="block text-xs text-zinc-400 hover:text-zinc-200">← {isEN ? "Back" : "Indietro"}</Link>
        </div>
      </div>
    );
  }

  const requestStep1 = async () => {
    setLoading(true);
    try {
      await api.post("/auth/admin/recovery/request", { email });
      toast.success(isEN ? "Pre-code sent" : "Pre-codice inviato");
      setStage("precode");
    } catch (e) {
      if (e.response?.status === 423) setBlocked(true);
      else toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verifyPrecode = async () => {
    setLoading(true);
    try {
      await api.post("/auth/admin/recovery/precode", { request_ref: ref.toUpperCase(), precode });
      toast.success(isEN ? "Pre-code OK · Launch code in 4 min" : "Pre-codice OK · Codice di lancio tra 4 min");
      setStage("waiting");
    } catch (e) {
      if (e.response?.status === 423) setBlocked(true);
      else toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const submitLaunch = async () => {
    if (newPw !== confirmPw) { toast.error(isEN ? "Passwords do not match" : "Le password non corrispondono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/admin/recovery/verify", { token: launchToken, code: launchCode, new_password: newPw });
      toast.success(isEN ? "Password reset · log in now" : "Password reimpostata · accedi ora");
      navigate("/login");
    } catch (e) {
      if (e.response?.status === 423) setBlocked(true);
      else toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-5" data-theme={theme} data-testid="admin-recover-root">
      <div className="absolute top-5 right-5 z-10"><ThemeSwitcher /></div>
      <div className="max-w-md w-full bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 space-y-5">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">{isEN ? "Account recovery" : "Recupero account"}</p>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#FFD700]" /> Rent Lupi</h1>
        </div>

        {stage === "request" && (
          <>
            <p className="text-zinc-400 text-sm">{isEN ? "Enter your admin email — you'll receive a pre-code on your secure channel." : "Inserisci la tua email admin — riceverai un pre-codice sul canale sicuro."}</p>
            <div>
              <Label className="text-xs text-zinc-400">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="admin-recover-email" />
            </div>
            <Button onClick={requestStep1} disabled={loading || !email} className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="admin-recover-request-button">
              {loading ? "..." : (isEN ? "Send pre-code" : "Invia pre-codice")}
            </Button>
          </>
        )}

        {stage === "precode" && (
          <>
            <p className="text-zinc-400 text-sm">{isEN ? "Enter the reference and pre-code you received." : "Inserisci riferimento e pre-codice ricevuti."}</p>
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "Reference" : "Riferimento"}</Label>
              <Input value={ref} onChange={(e) => setRef(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))} placeholder="ABCD2345" className="bg-zinc-950 border-zinc-800 mt-1 text-center text-lg tracking-[0.4em] font-mono uppercase" data-testid="admin-recover-ref" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "Pre-code" : "Pre-codice"}</Label>
              <Input value={precode} onChange={(e) => setPrecode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="000000" className="bg-zinc-950 border-zinc-800 mt-1 text-center text-2xl tracking-[0.5em] font-mono" data-testid="admin-recover-precode" />
            </div>
            <Button onClick={verifyPrecode} disabled={loading || ref.length !== 8 || precode.length !== 6} className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="admin-recover-precode-button">
              {loading ? "..." : (isEN ? "Verify pre-code" : "Verifica pre-codice")}
            </Button>
          </>
        )}

        {stage === "waiting" && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-5 text-center space-y-3" data-testid="admin-recover-waiting">
            <Hourglass className="w-10 h-10 mx-auto text-amber-400" />
            <p className="text-amber-200 text-sm leading-relaxed">
              {isEN
                ? "Pre-code accepted. The launch code will be delivered to your secure channel shortly. Tap the link in that message to complete the reset."
                : "Pre-codice accettato. Il codice di lancio arriverà sul canale sicuro a breve. Tocca il link in quel messaggio per completare il reset."}
            </p>
          </div>
        )}

        {stage === "launch" && (
          <>
            <p className="text-zinc-400 text-sm">{isEN ? "Enter the launch code and your new password." : "Inserisci il codice di lancio e la nuova password."}</p>
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "Launch code" : "Codice di lancio"}</Label>
              <Input value={launchCode} onChange={(e) => setLaunchCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))} placeholder="000000" className="bg-zinc-950 border-zinc-800 mt-1 text-center text-2xl tracking-[0.5em] font-mono" data-testid="admin-recover-code" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "New password" : "Nuova password"}</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="admin-recover-newpw" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">{isEN ? "Confirm new password" : "Conferma nuova password"}</Label>
              <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="admin-recover-confirmpw" />
            </div>
            <Button onClick={submitLaunch} disabled={loading || launchCode.length !== 6 || newPw.length < 8} className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="admin-recover-submit">
              <KeyRound className="w-4 h-4 mr-1" /> {loading ? "..." : (isEN ? "Reset password" : "Reimposta password")}
            </Button>
          </>
        )}

        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300">
          <ArrowLeft className="w-3 h-3" /> {isEN ? "Back to login" : "Torna al login"}
        </Link>
      </div>
    </div>
  );
}
